import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { NotificationBell } from './NotificationBell';
import InstallPWABanner from './InstallPWABanner';
import { getCoachUnreadCount } from '../lib/api';

import { WhatsNewSheet } from './WhatsNewSheet';
import {
    LayoutDashboard, Apple, Moon, Dumbbell, ListTodo, Plane, Trophy,
    MessageCircle, Settings as SettingsIcon, LogOut, Menu, X, Globe, HeartPulse, User, Shield, Watch, BarChart3, BookOpen, Clock, Target, HelpCircle
} from 'lucide-react';

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [coachUnread, setCoachUnread] = useState(0);
    const location = useLocation();

    // Poll coach unread badge: refetch on every route change so it clears
    // instantly after the user visits /coach (which calls mark-read on mount).
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        const fetchCount = async () => {
            try {
                const { data } = await getCoachUnreadCount();
                if (!cancelled) setCoachUnread(Number(data?.unread || 0));
            } catch { /* ignore network errors — badge is best-effort */ }
        };
        fetchCount();
        // Refresh every 5 minutes for users idling on a non-coach page
        const interval = setInterval(fetchCount, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [user, location.pathname]);

    const navItems = [
        { path: '/', icon: LayoutDashboard, label: t('nav_dashboard') },
        { path: '/nutrition', icon: Apple, label: t('nav_nutrition') },
        { path: '/sleep', icon: Moon, label: t('nav_sleep') },
        { path: '/fitness', icon: Dumbbell, label: t('nav_fitness') },
        { path: '/health', icon: HeartPulse, label: t('health_title') },
        { path: '/statistics', icon: BarChart3, label: language === 'de' ? 'Übersicht' : 'Statistics' },
        { path: '/history', icon: Clock, label: language === 'de' ? 'Verlauf' : 'History' },
        { path: '/goals', icon: Target, label: language === 'de' ? 'Ziele' : 'Goals' },
        { path: '/blog', icon: BookOpen, label: 'Blog' },
        { path: '/tasks', icon: ListTodo, label: t('nav_tasks') },
        { path: '/travel', icon: Plane, label: t('nav_travel') },
        { path: '/achievements', icon: Trophy, label: t('nav_achievements') },
        { path: '/coach', icon: MessageCircle, label: t('nav_coach') },
        { path: '/wearables', icon: Watch, label: language === 'de' ? 'Wearables' : 'Wearables' },
        { path: '/trust', icon: Shield, label: language === 'de' ? 'Qualitätsversprechen' : 'Trust & Safety' },
        { path: '/profile', icon: User, label: language === 'de' ? 'Mein Profil' : 'My Profile' },
        { path: '/help', icon: HelpCircle, label: language === 'de' ? 'Hilfe & Tutorials' : 'Help & Tutorials' },
        { path: '/settings', icon: SettingsIcon, label: t('settings_title') },
    ];

    // Mobile Bottom-Nav: dedicated 5 items with Coach center-stage so the
    // Nanouk AI coach is one tap away (previously buried in the hamburger menu).
    const bottomNavItems = [
        { path: '/', icon: LayoutDashboard, label: t('nav_dashboard') },
        { path: '/nutrition', icon: Apple, label: t('nav_nutrition') },
        { path: '/coach', icon: MessageCircle, label: t('nav_coach'), highlight: true },
        { path: '/fitness', icon: Dumbbell, label: t('nav_fitness') },
        { path: '/health', icon: HeartPulse, label: t('health_title') },
    ];

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-64 lg:overflow-y-auto lg:bg-card lg:border-r lg:border-border">
                <div className="flex h-full flex-col">
                    <div className="flex h-16 items-center gap-3 px-6 border-b border-border">
                        <div className="w-10 h-10 rounded-2xl overflow-hidden">
                            <img 
                                src="https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop" 
                                alt="Nanouk"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div>
                            <h1 className="font-display font-bold text-lg">{t('app_name')}</h1>
                            <p className="text-xs text-muted-foreground">{t('app_tagline')}</p>
                        </div>
                    </div>

                    <nav className="flex-1 px-3 py-4 space-y-1">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                                        isActive
                                            ? 'bg-primary text-primary-foreground'
                                            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                                    }`
                                }
                            >
                                <item.icon className="w-5 h-5" strokeWidth={2} />
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="p-4 border-t border-border">
                        <div className="flex items-center gap-3 mb-4">
                            <Avatar className="w-10 h-10">
                                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                                    {getInitials(user?.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{user?.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <NotificationBell />
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={toggleLanguage}
                                className="flex-1"
                                title={language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
                                data-testid="language-toggle-btn"
                            >
                                <span className="text-sm">{language === 'de' ? '🇬🇧' : '🇩🇪'}</span>
                            </Button>
                            <NavLink to="/settings" className="flex-1">
                                <Button variant="ghost" size="icon" className="w-full" data-testid="settings-btn">
                                    <SettingsIcon className="w-4 h-4" />
                                </Button>
                            </NavLink>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={logout}
                                className="flex-1 text-destructive hover:text-destructive"
                                data-testid="logout-btn"
                            >
                                <LogOut className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Mobile Header - with safe area padding for notch/dynamic island */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-xl border-b border-border pt-safe">
                <div className="flex items-center justify-between h-16 px-4">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl overflow-hidden">
                            <img 
                                src="https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop" 
                                alt="Nanouk"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <span className="font-display font-bold">{t('app_name')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <NotificationBell />
                        <Button variant="ghost" size="icon" onClick={toggleLanguage} data-testid="mobile-language-toggle">
                            <span className="text-sm">{language === 'de' ? '🇬🇧' : '🇩🇪'}</span>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
                            <Menu className="w-5 h-5" />
                        </Button>
                    </div>
                </div>
            </header>

            {/* Mobile Sidebar Overlay */}
            <AnimatePresence>
                {sidebarOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="lg:hidden fixed inset-0 z-50 bg-black/50"
                            onClick={() => setSidebarOpen(false)}
                        />
                        <motion.aside
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="lg:hidden fixed top-0 right-0 bottom-0 z-50 w-72 bg-card border-l border-border"
                        >
                            <div className="flex h-full flex-col">
                                <div className="flex h-16 items-center justify-between px-4 border-b border-border">
                                    <span className="font-display font-bold">Menu</span>
                                    <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                                        <X className="w-5 h-5" />
                                    </Button>
                                </div>
                                <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                                    {navItems.map((item) => (
                                        <NavLink
                                            key={item.path}
                                            to={item.path}
                                            onClick={() => setSidebarOpen(false)}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                                                    isActive
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                                                }`
                                            }
                                        >
                                            <item.icon className="w-5 h-5" strokeWidth={2} />
                                            {item.label}
                                        </NavLink>
                                    ))}
                                </nav>
                                <div className="p-4 border-t border-border">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Avatar className="w-10 h-10">
                                            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                                                {getInitials(user?.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{user?.name}</p>
                                            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <NavLink to="/settings" onClick={() => setSidebarOpen(false)} className="flex-1">
                                            <Button variant="outline" className="w-full gap-2">
                                                <SettingsIcon className="w-4 h-4" />
                                                {t('nav_settings')}
                                            </Button>
                                        </NavLink>
                                        <Button variant="outline" onClick={logout} className="text-destructive hover:text-destructive">
                                            <LogOut className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>

            <main className="lg:pl-64 lg:pt-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4rem)' }}>
                <div className="min-h-screen">{children}</div>
            </main>

            {/* Mobile Bottom Navigation */}
            <nav className="lg:hidden bottom-nav" data-testid="bottom-nav">
                <div className="flex items-end justify-around pt-1.5 pb-0.5">
                    {bottomNavItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            data-testid={`bottom-nav-${item.path.replace('/', '') || 'dashboard'}`}
                            className={({ isActive }) =>
                                item.highlight
                                    ? `relative -mt-5 flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-full transition-colors`
                                    : `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-colors ${
                                          isActive ? 'text-primary' : 'text-muted-foreground'
                                      }`
                            }
                        >
                            {({ isActive }) => item.highlight ? (
                                <>
                                    <div className={`relative w-12 h-12 rounded-full flex items-center justify-center shadow-lg shadow-primary/30 ring-4 ring-card transition-all ${
                                        isActive
                                            ? 'bg-primary text-primary-foreground scale-105'
                                            : 'bg-primary text-primary-foreground'
                                    }`}>
                                        <item.icon className="w-6 h-6" strokeWidth={2.2} />
                                        {coachUnread > 0 && (
                                            <span
                                                className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-card animate-pulse-soft"
                                                data-testid="coach-unread-badge"
                                                aria-label={`${coachUnread} new insights`}
                                            >
                                                {coachUnread > 9 ? '9+' : coachUnread}
                                            </span>
                                        )}
                                    </div>
                                    <span className={`text-[10px] font-semibold leading-none ${isActive ? 'text-primary' : 'text-foreground'}`}>{item.label}</span>
                                </>
                            ) : (
                                <>
                                    <item.icon className="w-5 h-5" strokeWidth={2} />
                                    <span className="text-[10px] font-medium leading-none">{item.label}</span>
                                </>
                            )}
                        </NavLink>
                    ))}
                </div>
            </nav>

            {/* PWA Install Banner (auto-hides if installed or recently dismissed) */}
            <InstallPWABanner />

            {/* Auto-pops once per release on first visit after login */}
            {user && <WhatsNewSheet />}
        </div>
    );
};
